import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Building2, Truck, Users, Check, ArrowRight, ArrowLeft, CreditCard, Minus, Plus } from 'lucide-react';
import { useAuthStore } from '../store/auth.store';
import { devicesApi, usersApi, billingApi } from '../services/api';

interface OnboardingData {
  organizationSetup: boolean;
  firstDevice: {
    imei: string;
    name: string;
    plate: string;
  };
  inviteUsers: Array<{
    email: string;
    role: string;
  }>;
}

interface Plan {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  priceOneShot: number;
  priceYearly: number;
  allowOneShot: boolean;
  allowYearly: boolean;
  usersPerTracker: number;
  geofencesPerTracker: number;
  retentionDays: number;
  features: string[];
  stripePriceIdOneShot?: string;
  stripePriceIdYearly?: string;
  highlightLabel?: string;
  priceDisplay?: 'HT' | 'TTC';
}

const steps = [
  { id: 1, titleKey: 'onboarding.step_welcome_short', icon: MapPin },
  { id: 2, titleKey: 'onboarding.step_first_vehicle', icon: Truck },
  { id: 3, titleKey: 'onboarding.step_team_short', icon: Users },
  { id: 4, titleKey: 'onboarding.step_plan_short', icon: CreditCard },
  { id: 5, titleKey: 'onboarding.step_finished', icon: Check },
];

export default function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<OnboardingData>({
    organizationSetup: true,
    firstDevice: { imei: '', name: '', plate: '' },
    inviteUsers: [],
  });
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('USER');

  // État de la sélection de plan
  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [checkoutProcessing, setCheckoutProcessing] = useState(false);
  const [pricingType, setPricingType] = useState<'ONESHOT' | 'YEARLY'>('YEARLY');

  // Gérer le retour depuis le checkout Stripe
  useEffect(() => {
    const stepParam = searchParams.get('step');
    const checkout = searchParams.get('success') || searchParams.get('checkout');
    if (stepParam) {
      setCurrentStep(parseInt(stepParam));
    }
    if (checkout === 'true') {
      // L'utilisateur a complété le checkout Stripe, aller à la dernière étape
      setCurrentStep(5);
    }
  }, [searchParams]);

  // Charger les plans quand on arrive au step 4
  useEffect(() => {
    if (currentStep === 4 && plans.length === 0) {
      setPlansLoading(true);
      billingApi.getPlans()
        .then((res) => setPlans(res.data))
        .catch((err) => console.error('Erreur chargement plans:', err))
        .finally(() => setPlansLoading(false));
    }
  }, [currentStep, plans.length]);

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const addUserToInvite = () => {
    if (newUserEmail.trim()) {
      setData({
        ...data,
        inviteUsers: [...data.inviteUsers, { email: newUserEmail.trim(), role: newUserRole }],
      });
      setNewUserEmail('');
      setNewUserRole('USER');
    }
  };

  const removeUserToInvite = (index: number) => {
    setData({
      ...data,
      inviteUsers: data.inviteUsers.filter((_, i) => i !== index),
    });
  };

  const handleAddDevice = async () => {
    if (!data.firstDevice.imei.trim()) {
      nextStep();
      return;
    }

    setLoading(true);
    try {
      await devicesApi.create({
        imei: data.firstDevice.imei,
        name: data.firstDevice.name || t('onboarding.default_vehicle_name', { suffix: data.firstDevice.imei.slice(-4) }),
        plate: data.firstDevice.plate || undefined,
      });
      nextStep();
    } catch (error) {
      console.error('Erreur ajout véhicule:', error);
      // Continuer quand même
      nextStep();
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUsers = async () => {
    if (data.inviteUsers.length === 0) {
      nextStep();
      return;
    }

    setLoading(true);
    try {
      for (const invite of data.inviteUsers) {
        await usersApi.create({
          email: invite.email,
          password: Math.random().toString(36).slice(-12), // Temp password
          firstName: t('onboarding.default_first_name'),
          lastName: '',
          role: invite.role,
        });
      }
      nextStep();
    } catch (error) {
      console.error('Erreur invitation utilisateurs:', error);
      // Continuer quand même
      nextStep();
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (plan: Plan) => {
    const isFree = plan.priceOneShot === 0 && plan.priceYearly === 0;

    if (isFree) {
      nextStep();
      return;
    }

    const priceId = pricingType === 'YEARLY' ? plan.stripePriceIdYearly : plan.stripePriceIdOneShot;
    if (!priceId) {
      alert('Ce plan n\'est pas encore disponible pour l\'achat en ligne');
      nextStep();
      return;
    }

    setCheckoutProcessing(true);
    try {
      const res = await billingApi.createCheckout(priceId, quantity);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        // Stripe non configuré, passer
        nextStep();
      }
    } catch (error) {
      console.error('Erreur checkout:', error);
      // Si Stripe n'est pas configuré, continuer
      nextStep();
    } finally {
      setCheckoutProcessing(false);
    }
  };

  const handleFinish = () => {
    localStorage.setItem('onboarding_completed', 'true');
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="py-6 px-8 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-cyan flex items-center justify-center shadow-glow-sm">
            <MapPin className="h-5 w-5 text-white" />
          </div>
          <div>
            <span className="text-xl font-bold text-white">TrackFleet</span>
            <span className="block text-xs text-gray-500">Configuration initiale</span>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="px-8 py-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    currentStep >= step.id
                      ? 'bg-cyan-500 text-white'
                      : 'bg-white text-gray-400'
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <step.icon className="h-5 w-5" />
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`h-0.5 w-12 md:w-20 mx-2 ${
                      currentStep > step.id ? 'bg-cyan-500' : 'bg-gray-100'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            {steps.map((step) => (
              <span
                key={step.id}
                className={`text-xs ${
                  currentStep >= step.id ? 'text-cyan-400' : 'text-gray-400'
                }`}
              >
                {t(step.titleKey)}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-lg">
          {/* Step 1: Welcome */}
          {currentStep === 1 && (
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center mx-auto mb-6">
                <Building2 className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">
                {t('onboarding.welcome_title')}
              </h1>
              <p className="text-gray-500 mb-2">
                {t('onboarding.welcome_greeting', { name: user?.firstName })}
              </p>
              <p className="text-gray-400 mb-8">
                {t('onboarding.welcome_setup', { tenant: user?.tenantName })}
              </p>
              <div className="card-dark p-6 text-left mb-8">
                <h3 className="text-white font-medium mb-4">{t('onboarding.what_we_do')}</h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-gray-600">
                    <Truck className="h-5 w-5 text-cyan-500" />
                    {t('onboarding.step_add_vehicle')}
                  </li>
                  <li className="flex items-center gap-3 text-gray-600">
                    <Users className="h-5 w-5 text-purple-500" />
                    {t('onboarding.step_invite_team')}
                  </li>
                  <li className="flex items-center gap-3 text-gray-600">
                    <CreditCard className="h-5 w-5 text-yellow-500" />
                    {t('onboarding.step_choose_plan')}
                  </li>
                  <li className="flex items-center gap-3 text-gray-600">
                    <Check className="h-5 w-5 text-green-500" />
                    {t('onboarding.step_start_tracking')}
                  </li>
                </ul>
              </div>
              <button onClick={nextStep} className="btn-primary w-full flex items-center justify-center gap-2">
                {t('onboarding.start')}
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Step 2: First Device */}
          {currentStep === 2 && (
            <div>
              <div className="w-16 h-16 rounded-xl bg-cyan-500/20 flex items-center justify-center mx-auto mb-6">
                <Truck className="h-8 w-8 text-cyan-500" />
              </div>
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                {t('onboarding.device_step_title')}
              </h2>
              <p className="text-gray-500 text-center mb-8">
                {t('onboarding.device_step_subtitle')}
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-500 text-sm mb-1">
                    {t('onboarding.field_imei')} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={data.firstDevice.imei}
                    onChange={(e) =>
                      setData({
                        ...data,
                        firstDevice: { ...data.firstDevice, imei: e.target.value },
                      })
                    }
                    placeholder="Ex: 867730050123456"
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    {t('onboarding.imei_hint')}
                  </p>
                </div>
                <div>
                  <label className="block text-gray-500 text-sm mb-1">{t('onboarding.field_vehicle_name')}</label>
                  <input
                    type="text"
                    value={data.firstDevice.name}
                    onChange={(e) =>
                      setData({
                        ...data,
                        firstDevice: { ...data.firstDevice, name: e.target.value },
                      })
                    }
                    placeholder="Ex: Camion 1"
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  />
                </div>
                <div>
                  <label className="block text-gray-500 text-sm mb-1">{t('onboarding.field_plate')}</label>
                  <input
                    type="text"
                    value={data.firstDevice.plate}
                    onChange={(e) =>
                      setData({
                        ...data,
                        firstDevice: { ...data.firstDevice, plate: e.target.value },
                      })
                    }
                    placeholder="Ex: AB-123-CD"
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                <button onClick={prevStep} className="btn-secondary flex-1 flex items-center justify-center gap-2">
                  <ArrowLeft className="h-5 w-5" />
                  {t('onboarding.back')}
                </button>
                <button
                  onClick={handleAddDevice}
                  disabled={loading}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      {data.firstDevice.imei ? t('onboarding.add') : t('onboarding.skip')}
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Invite Team */}
          {currentStep === 3 && (
            <div>
              <div className="w-16 h-16 rounded-xl bg-purple-500/20 flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-purple-500" />
              </div>
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                {t('onboarding.team_step_title')}
              </h2>
              <p className="text-gray-500 text-center mb-8">
                {t('onboarding.team_step_subtitle')}
              </p>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder={t('onboarding.team_email_placeholder')}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 flex-1"
                  />
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  >
                    <option value="USER">{t('onboarding.role_user')}</option>
                    <option value="MANAGER">{t('onboarding.role_manager')}</option>
                    <option value="ADMIN">{t('onboarding.role_admin')}</option>
                  </select>
                  <button onClick={addUserToInvite} className="btn-secondary px-4">
                    +
                  </button>
                </div>
                {data.inviteUsers.length > 0 && (
                  <div className="card-dark p-4 space-y-2">
                    {data.inviteUsers.map((invite, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                        <div>
                          <p className="text-white">{invite.email}</p>
                          <p className="text-gray-400 text-xs">{invite.role}</p>
                        </div>
                        <button
                          onClick={() => removeUserToInvite(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-gray-400 text-sm text-center">
                  {t('onboarding.team_later_hint')}
                </p>
              </div>
              <div className="flex gap-3 mt-8">
                <button onClick={prevStep} className="btn-secondary flex-1 flex items-center justify-center gap-2">
                  <ArrowLeft className="h-5 w-5" />
                  {t('onboarding.back')}
                </button>
                <button
                  onClick={handleInviteUsers}
                  disabled={loading}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      {data.inviteUsers.length > 0 ? t('onboarding.invite_or_skip') : t('onboarding.skip_step')}
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Choose Plan */}
          {currentStep === 4 && (
            <div>
              <div className="w-16 h-16 rounded-xl bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
                <CreditCard className="h-8 w-8 text-yellow-500" />
              </div>
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                {t('onboarding.plan_step_title')}
              </h2>
              <p className="text-gray-500 text-center mb-4">
                {t('onboarding.plan_step_subtitle')}
              </p>

              {/* Toggle OneShot / Yearly */}
              <div className="flex items-center justify-center mb-6">
                <div className="inline-flex rounded-lg bg-gray-800 p-1">
                  <button
                    onClick={() => { setPricingType('ONESHOT'); setSelectedPlanId(null); }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      pricingType === 'ONESHOT'
                        ? 'bg-gray-700 text-white shadow-sm'
                        : 'text-gray-400 hover:text-gray-300'
                    }`}
                  >
                    {t('onboarding.toggle_oneshot')}
                  </button>
                  <button
                    onClick={() => { setPricingType('YEARLY'); setSelectedPlanId(null); }}
                    className={`relative px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      pricingType === 'YEARLY'
                        ? 'bg-gray-700 text-white shadow-sm'
                        : 'text-gray-400 hover:text-gray-300'
                    }`}
                  >
                    {t('onboarding.toggle_yearly')}
                    {plans.some((p) => p.allowOneShot && p.allowYearly && p.priceYearly < p.priceOneShot) && (
                      <span className="ml-2 px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full font-medium">
                        {t('onboarding.savings_badge')}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {plansLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {plans
                    .filter((plan) => {
                      const isFree = plan.priceOneShot === 0 && plan.priceYearly === 0;
                      if (isFree) return true;
                      return pricingType === 'YEARLY' ? plan.allowYearly : plan.allowOneShot;
                    })
                    .sort((a, b) => {
                      const priceA = pricingType === 'YEARLY' ? a.priceYearly : a.priceOneShot;
                      const priceB = pricingType === 'YEARLY' ? b.priceYearly : b.priceOneShot;
                      return priceA - priceB;
                    })
                    .map((plan) => {
                      const isFree = plan.priceOneShot === 0 && plan.priceYearly === 0;
                      const displayPrice = pricingType === 'YEARLY' ? plan.priceYearly : plan.priceOneShot;
                      const monthlyEquiv = plan.priceYearly / 12;
                      const isSelected = selectedPlanId === plan.id;
                      const savingsPercent = plan.allowOneShot && plan.allowYearly && plan.priceOneShot > 0
                        ? Math.round((1 - plan.priceYearly / plan.priceOneShot) * 100)
                        : 0;

                      return (
                        <div
                          key={plan.id}
                          onClick={() => setSelectedPlanId(plan.id)}
                          className={`card-dark p-4 cursor-pointer border-2 transition-colors ${
                            isSelected ? 'border-cyan-500' : 'border-transparent hover:border-gray-600'
                          } ${plan.highlightLabel ? 'ring-1 ring-cyan-500/30' : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                isSelected ? 'border-cyan-500 bg-cyan-500' : 'border-gray-400'
                              }`}>
                                {isSelected && <Check className="h-3 w-3 text-white" />}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-white font-semibold">{plan.displayName}</span>
                                  {plan.highlightLabel && (
                                    <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                                      {plan.highlightLabel}
                                    </span>
                                  )}
                                </div>
                                {plan.description && (
                                  <p className="text-gray-400 text-xs mt-0.5">{plan.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              {isFree ? (
                                <span className="text-white font-bold">{t('onboarding.free')}</span>
                              ) : pricingType === 'YEARLY' ? (
                                <div>
                                  <span className="text-white font-bold">{displayPrice}€</span>
                                  <span className="text-gray-400 text-xs">{t('onboarding.per_tracker_year')}</span>
                                  <span className="text-gray-500 text-xs ml-1">{plan.priceDisplay || 'HT'}</span>
                                  <p className="text-gray-500 text-xs">{t('onboarding.monthly_equivalent', { amount: monthlyEquiv.toFixed(0) })}</p>
                                  {savingsPercent > 0 && (
                                    <p className="text-green-400 text-xs font-medium">-{savingsPercent}%</p>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <span className="text-white font-bold">{displayPrice}€</span>
                                  <span className="text-gray-400 text-xs">{t('onboarding.per_tracker')}</span>
                                  <span className="text-gray-500 text-xs ml-1">{plan.priceDisplay || 'HT'}</span>
                                  <p className="text-gray-500 text-xs">{t('onboarding.oneshot_payment')}</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Detail panel shown when selected */}
                          {isSelected && !isFree && (
                            <div className="mt-3 pt-3 border-t border-gray-700">
                              <div className="flex items-center gap-3 mb-2">
                                <label className="text-gray-400 text-sm">{t('onboarding.trackers_quantity')}</label>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setQuantity(Math.max(1, quantity - 1)); }}
                                    className="w-7 h-7 rounded bg-gray-700 hover:bg-gray-600 flex items-center justify-center text-white"
                                  >
                                    <Minus className="h-3 w-3" />
                                  </button>
                                  <input
                                    type="number"
                                    min={1}
                                    value={quantity}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="w-14 text-center px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                                  />
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setQuantity(quantity + 1); }}
                                    className="w-7 h-7 rounded bg-gray-700 hover:bg-gray-600 flex items-center justify-center text-white"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-xs text-gray-400">
                                <span>{t('onboarding.spec_devices', { count: quantity })}</span>
                                <span>{t('onboarding.spec_users', { count: quantity * plan.usersPerTracker })}</span>
                                <span>{t('onboarding.spec_geofences', { count: quantity * plan.geofencesPerTracker })}</span>
                              </div>
                              {quantity > 1 && (
                                <p className="text-cyan-400 text-xs mt-1">
                                  {pricingType === 'YEARLY'
                                    ? t('onboarding.total_yearly', {
                                        amount: (displayPrice * quantity).toFixed(0),
                                        taxLabel: plan.priceDisplay || 'HT',
                                        monthly: (monthlyEquiv * quantity).toFixed(0),
                                      })
                                    : t('onboarding.total_oneshot', {
                                        amount: (displayPrice * quantity).toFixed(0),
                                        taxLabel: plan.priceDisplay || 'HT',
                                      })}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}

              <div className="flex gap-3 mt-8">
                <button onClick={prevStep} className="btn-secondary flex-1 flex items-center justify-center gap-2">
                  <ArrowLeft className="h-5 w-5" />
                  {t('onboarding.back')}
                </button>
                <button
                  onClick={() => {
                    const selectedPlan = plans.find((p) => p.id === selectedPlanId);
                    if (selectedPlan) {
                      handleSelectPlan(selectedPlan);
                    } else {
                      nextStep();
                    }
                  }}
                  disabled={checkoutProcessing}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {checkoutProcessing ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      {selectedPlanId ? t('onboarding.continue') : t('onboarding.continue_free')}
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Complete */}
          {currentStep === 5 && (
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <Check className="h-10 w-10 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                {t('onboarding.complete_title')}
              </h2>
              <p className="text-gray-500 mb-8">
                {t('onboarding.complete_subtitle')}
              </p>
              <div className="card-dark p-6 text-left mb-8">
                <h3 className="text-white font-medium mb-4">{t('onboarding.summary')}</h3>
                <ul className="space-y-3 text-gray-600">
                  {data.firstDevice.imei && (
                    <li className="flex items-center gap-3">
                      <Truck className="h-5 w-5 text-cyan-500" />
                      {t('onboarding.complete_vehicle_added', { label: data.firstDevice.name || data.firstDevice.imei })}
                    </li>
                  )}
                  {data.inviteUsers.length > 0 && (
                    <li className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-purple-500" />
                      {t('onboarding.complete_users_invited', { count: data.inviteUsers.length })}
                    </li>
                  )}
                  <li className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-green-500" />
                    {t('onboarding.complete_ready')}
                  </li>
                </ul>
              </div>
              <button onClick={handleFinish} className="btn-primary w-full flex items-center justify-center gap-2">
                {t('onboarding.go_to_dashboard')}
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
